import {Component, Inject} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {NotificationService} from "../../../core/services/notification.service";
import {SharedModule} from "../../../shared/shared.module";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {NgIf} from "@angular/common";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {FormsModule} from "@angular/forms";
import {PushProgressService} from "../../../core/services/push-progress.service";
import {AuthService} from "../../../core/services/auth.service";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationDialogComponent} from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";

export interface IPushExercisesToLocationDialogData {
  exercisesIds: string[];
}

@Component({
  selector: "app-push-exercises-to-location-dialog",
  templateUrl: "./push-exercises-to-location-dialog.component.html",
  standalone: true,
  imports: [
    SharedModule,
    MatProgressSpinnerModule,
    NgIf,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    FormsModule
  ],
  styleUrls: ["./push-exercises-to-location-dialog.component.scss"]
})
export class PushExercisesToLocationDialog {
  isLoading = false;
  /** Uncheck to push silently, e.g. when correcting a mistake. */
  notifyUsers = true;
  readonly sourceLocationId = AuthService.getSelectedLocationId();

  constructor(
      @Inject(MAT_DIALOG_DATA) private data: IPushExercisesToLocationDialogData,
      private dialogRef: MatDialogRef<PushExercisesToLocationDialog>,
      private messageService: NotificationService,
      private progress: PushProgressService,
      private dialog: MatDialog,
  ) {}

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(locationIds: string[]): void {
    this.isLoading = true;
    this.progress.create("Exercise",
        {
          exerciseIds: this.data.exercisesIds,
          locationIds: locationIds,
          toAllLocations: false,
          notifyUsers: this.notifyUsers
        }).subscribe({
      next: (res) => {
        this.dialogRef.close(res);
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.handleStartError(error);
      }
    })
  }

  pushToAll(): void {
    const count = this.data.exercisesIds.length;
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: "Push to all active locations?",
        message: `Push ${count} selected exercise${count === 1 ? "" : "s"} to every active, non-deleted location except the current source location?`,
        confirmButtonLabel: "Start push",
      },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.isLoading = true;
      this.progress.create("Exercise", {
        exerciseIds: this.data.exercisesIds,
        locationIds: [],
        toAllLocations: true,
        notifyUsers: this.notifyUsers,
      }).subscribe({
        next: (res) => {
          this.dialogRef.close(res);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.handleStartError(error);
        },
      });
    });
  }

  get exerciseCount(): number {
    return this.data.exercisesIds?.length ?? 0;
  }

  get confirmationPrefix(): string {
    const count = this.data.exercisesIds.length;
    return `Push ${count} selected exercise${count === 1 ? "" : "s"}`;
  }

  private handleStartError(error: any): void {
    if (error?.status === 409) {
      this.messageService.warn("Another exercise push is already running.");
      this.progress.openActiveJobIfOwned("Exercise");
      return;
    }
    this.messageService.error(error);
  }
}
